import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react'
import WaveSurfer, { WaveSurferOptions } from 'wavesurfer.js'
import WsRecordPlugin from '@/core/classes/WsRecordPlugin'

import Box from '@mui/material/Box'

import { getPalette } from '@/assets/themes/palette'

const palette = getPalette()

type LiveAudioVisualizerProps = {
  stream?: MediaStream
}

export type LiveAudioVisualizerHandle = {
  start: WsRecordPlugin['startMic']
  stop: () => void
}

const waveformHeight = 200

const defaultWavesurferOptions: Omit<WaveSurferOptions, 'container'> = {
  cursorWidth: 0,
  waveColor: palette.common.white,
  progressColor: 'grey',
  height: waveformHeight,
  interact: false,
  normalize: false,
  barHeight: 4,
}

export const LiveAudioVisualizer = forwardRef<LiveAudioVisualizerHandle, LiveAudioVisualizerProps>(function LiveAudioVisualizer(props, ref) {
  const { stream } = props

  const audioRecorderRef = useRef<WsRecordPlugin>(WsRecordPlugin.create({ scrollingWaveform: true }))
  const waveSurferRef = useRef<WaveSurfer | null>(null)
  const waveSurferContainerRef = useRef<HTMLDivElement>(null)

  const [isRecording, setIsRecording] = useState(false)

  useImperativeHandle(ref, () => ({
    start,
    stop,
  }))

  const createWaveSurfer = useCallback((options: Omit<WaveSurferOptions, 'container'> = defaultWavesurferOptions) => {
    const container = waveSurferContainerRef.current

    if (!container) {
      throw new Error('Missing container to create Wavesurfer instance')
    }

    const ws = WaveSurfer.create({ ...options, container })

    const audioRecorder = ws.registerPlugin(audioRecorderRef.current)

    audioRecorder.on('record-start', () => {
      setIsRecording(true)
    })

    audioRecorder.on('record-end', () => {
      setIsRecording(false)
    })

    return ws
  }, [])

  useLayoutEffect(() => {
    const ws = createWaveSurfer()
    waveSurferRef.current = ws
    return () => {
      ws.destroy()
    }
  }, [createWaveSurfer])

  useEffect(() => {
    const audioRecorder = audioRecorderRef.current
    if (!audioRecorder || !stream) return

    if (!waveSurferRef.current) waveSurferRef.current = createWaveSurfer()

    WsRecordPlugin.visualizeStream(audioRecorder, stream)

    return () => {
      audioRecorder.destroy()
      audioRecorderRef.current = WsRecordPlugin.create({ scrollingWaveform: true })
      waveSurferRef.current?.destroy()
      waveSurferRef.current = null
    }
  }, [createWaveSurfer, stream])

  function start(options?: MediaTrackConstraints) {
    return audioRecorderRef.current.startMic(options)
  }

  function stop() {
    audioRecorderRef.current.destroy()
    audioRecorderRef.current = WsRecordPlugin.create({ scrollingWaveform: true })
    waveSurferRef.current?.destroy()
    waveSurferRef.current = createWaveSurfer()
  }

  return (
    <Box
      ref={waveSurferContainerRef}
      component="div"
      width="100%"
      sx={[
        {
          background: palette.primary.main,
          height: `${waveformHeight}px`,
          position: 'relative',
          overflow: 'auto hidden',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        },
        !isRecording && {
          '&::before': {
            content: '""',
            width: '100%',
            height: '2px',
            background: palette.common.white,
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
          },
        },
      ]}
    />
  )
})

export default LiveAudioVisualizer
